import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { ChevronsUpDown } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { useState as useLocalState } from "react";
import type { PlayoutSource } from "@/hooks/useBridgePlayout";
import {
  usePlayoutConfig,
  usePlayoutSources,
  useUpdatePlayoutConfig,
} from "@/hooks/useBridgePlayout";

interface PlayoutSettingsProps {
  bridgeUrl: string | undefined | null;
  /** ATEM connections in this studio, for the connection picker */
  atemConnections: Array<{ id: string; name: string }>;
  /** vMix servers, for video cut-ins */
  vmixConnections: Array<{ id: string; name: string }>;
}

/**
 * Studio-level switcher wiring. One record per studio, shared by every playout:
 * this describes how the room is patched, not anything creative about a graphic.
 */
export function PlayoutSettings({
  bridgeUrl,
  atemConnections,
  vmixConnections,
}: PlayoutSettingsProps) {
  const { data } = usePlayoutConfig(bridgeUrl);
  const update = useUpdatePlayoutConfig(bridgeUrl);
  const { data: sources } = usePlayoutSources(
    bridgeUrl,
    !!data?.config.connection_id,
  );

  const [form, setForm] = useState({
    connection_id: "",
    me_index: 1,
    ssrc_id: 0,
    program_source: 0,
    media_player: 0,
    vmix_connection_id: "",
    cutin_atem_input: "",
  });

  useEffect(() => {
    if (!data) return;
    setForm({
      connection_id: data.config.connection_id ?? "",
      me_index: data.config.me_index,
      ssrc_id: data.config.ssrc_id,
      program_source: data.config.program_source,
      media_player: data.config.media_player,
      vmix_connection_id: data.config.vmix_connection_id ?? "",
      cutin_atem_input:
        data.config.cutin_atem_input !== undefined
          ? String(data.config.cutin_atem_input)
          : "",
    });
  }, [data]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Switcher wiring</CardTitle>
        <CardDescription>
          Shared by every playout in this studio.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {data?.atem_offline && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>ATEM is in offline mock mode</AlertTitle>
            <AlertDescription>
              The bridge cannot reach the switcher, so nothing would actually
              reach air. Runs are blocked until a real ATEM answers.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>ATEM connection</Label>
            <Select
              value={form.connection_id}
              onValueChange={(value) =>
                setForm((f) => ({ ...f, connection_id: value }))
              }
            >
              <SelectTrigger data-testid="playout-connection">
                <SelectValue placeholder="Select an ATEM" />
              </SelectTrigger>
              <SelectContent>
                {atemConnections.map((connection) => (
                  <SelectItem key={connection.id} value={connection.id}>
                    {connection.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Mix effect</Label>
            <Select
              value={String(form.me_index)}
              onValueChange={(value) =>
                setForm((f) => ({ ...f, me_index: Number(value) }))
              }
            >
              <SelectTrigger data-testid="playout-me">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[0, 1, 2, 3].map((index) => (
                  <SelectItem key={index} value={String(index)}>
                    ME{index + 1}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Program source</Label>
            {sources?.sources?.length ? (
              <SourceCombobox
                sources={sources.sources}
                value={form.program_source}
                onChange={(value) =>
                  setForm((f) => ({ ...f, program_source: value }))
                }
              />
            ) : (
              <Input
                type="number"
                value={form.program_source}
                onChange={(event) =>
                  setForm((f) => ({
                    ...f,
                    program_source: Number(event.target.value),
                  }))
                }
                placeholder="Source number"
              />
            )}
            <p className="text-xs text-muted-foreground">
              The feed that shrinks into the notch. Often an ME re-entry, but on
              switchers where that is not allowed it arrives on a plain SDI
              input instead — so every source is listed.
            </p>
          </div>

          <div className="space-y-2">
            <Label>SuperSource</Label>
            <Select
              value={String(form.ssrc_id)}
              onValueChange={(value) =>
                setForm((f) => ({ ...f, ssrc_id: Number(value) }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[0, 1].map((index) => (
                  <SelectItem key={index} value={String(index)}>
                    SuperSource {index + 1}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Media player</Label>
            <Select
              value={String(form.media_player)}
              onValueChange={(value) =>
                setForm((f) => ({ ...f, media_player: Number(value) }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[0, 1, 2, 3].map((index) => (
                  <SelectItem key={index} value={String(index)}>
                    Media Player {index + 1}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Feeds the SuperSource art fill. The pool slot is per artwork.
            </p>
          </div>
        </div>

        <div className="space-y-4 border-t pt-4">
          <div>
            <h4 className="text-sm font-medium">Video cut-ins</h4>
            <p className="text-xs text-muted-foreground">
              Videos are loaded onto vMix by hand. Playout restarts the input,
              takes it to vMix program, waits for confirmation, then squeezes
              the program window over it.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>vMix server</Label>
              <Select
                value={form.vmix_connection_id}
                onValueChange={(value) =>
                  setForm((f) => ({ ...f, vmix_connection_id: value }))
                }
              >
                <SelectTrigger data-testid="playout-vmix-connection">
                  <SelectValue placeholder="Select a vMix server" />
                </SelectTrigger>
                <SelectContent>
                  {vmixConnections.map((connection) => (
                    <SelectItem key={connection.id} value={connection.id}>
                      {connection.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {vmixConnections.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  Add a vMix device under Devices first.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Cut-in ATEM input</Label>
              {sources?.sources?.length ? (
                <SourceCombobox
                  sources={sources.sources}
                  value={Number(form.cutin_atem_input) || 0}
                  onChange={(value) =>
                    setForm((f) => ({
                      ...f,
                      cutin_atem_input: String(value),
                    }))
                  }
                />
              ) : (
                <Input
                  type="number"
                  value={form.cutin_atem_input}
                  onChange={(event) =>
                    setForm((f) => ({
                      ...f,
                      cutin_atem_input: event.target.value,
                    }))
                  }
                  placeholder="ATEM input carrying vMix output"
                />
              )}
              <p className="text-xs text-muted-foreground">
                The ATEM input the vMix output is patched into.
              </p>
            </div>
          </div>
        </div>

        {data?.video_mode && (
          <p className="text-sm text-muted-foreground">
            Switcher video mode: {data.video_mode.width}×
            {data.video_mode.height}
          </p>
        )}

        <Button
          onClick={() =>
            update.mutate({
              ...form,
              connection_id: form.connection_id || undefined,
              vmix_connection_id: form.vmix_connection_id || undefined,
              cutin_atem_input: form.cutin_atem_input
                ? Number(form.cutin_atem_input)
                : undefined,
            })
          }
          disabled={update.isPending}
        >
          Save settings
        </Button>
      </CardContent>
    </Card>
  );
}

/** Searchable picker over every switcher source. */
function SourceCombobox({
  sources,
  value,
  onChange,
}: {
  sources: PlayoutSource[];
  value: number;
  onChange: (value: number) => void;
}) {
  const [open, setOpen] = useLocalState(false);
  const current = sources.find((source) => source.source === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          className="w-full justify-between font-normal"
          data-testid="playout-program-source"
        >
          <span className="truncate">
            {current
              ? `${current.name} (${current.source})`
              : "Select a source"}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command
          filter={(itemValue, search) =>
            itemValue.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
          }
        >
          <CommandInput placeholder="Search sources..." />
          <CommandList className="max-h-[280px]">
            <CommandEmpty>No source found.</CommandEmpty>
            <CommandGroup>
              {sources.map((source) => (
                <CommandItem
                  key={source.source}
                  value={`${source.name} ${source.shortName} ${source.source}`}
                  onSelect={() => {
                    onChange(source.source);
                    setOpen(false);
                  }}
                >
                  <span className="flex-1 truncate">{source.name}</span>
                  <span className="ml-2 font-mono text-xs text-muted-foreground">
                    {source.source}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
